import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Mail, Phone, Building, Clock, AlertCircle, Eye, Calendar } from "lucide-react";
import { format, startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from "date-fns";

interface Lead {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  company: string | null;
  message: string;
  status: string;
  priority: string;
  source: string;
  notes: string | null;
  tags: string[] | null;
  last_contact_date: string | null;
  next_follow_up_date: string | null;
  created_at: string;
  updated_at: string;
}

interface LeadKanbanBoardProps {
  leads: Lead[];
  onStatusChange: (leadId: string, newStatus: string) => void;
  onLeadClick: (lead: Lead) => void;
}

type DateFilter = 'all' | 'today' | 'week' | 'month';

const statusColumns = [
  { id: 'new', title: 'New', color: 'border-primary/30 bg-primary/5' },
  { id: 'contacted', title: 'Contacted', color: 'border-accent/30 bg-accent/5' },
  { id: 'qualified', title: 'Qualified', color: 'border-secondary/30 bg-secondary/5' },
  { id: 'converted', title: 'Converted', color: 'border-accent/30 bg-accent/5' },
  { id: 'closed', title: 'Closed', color: 'border-muted/30 bg-muted/5' },
];

export function LeadKanbanBoard({ leads, onStatusChange, onLeadClick }: LeadKanbanBoardProps) {
  const [draggedLead, setDraggedLead] = useState<Lead | null>(null);
  const [dateFilter, setDateFilter] = useState<DateFilter>('all');

  const handleDragStart = (e: React.DragEvent, lead: Lead) => {
    setDraggedLead(lead);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, newStatus: string) => {
    e.preventDefault();
    if (draggedLead && draggedLead.status !== newStatus) {
      onStatusChange(draggedLead.id, newStatus);
    }
    setDraggedLead(null);
  };

  const filterLeadsByDate = (leads: Lead[]) => {
    if (dateFilter === 'all') return leads;

    const now = new Date();
    let startDate: Date;
    let endDate: Date;

    switch (dateFilter) {
      case 'today':
        startDate = startOfDay(now);
        endDate = endOfDay(now);
        break;
      case 'week':
        startDate = startOfWeek(now, { weekStartsOn: 1 }); // Monday
        endDate = endOfWeek(now, { weekStartsOn: 1 });
        break;
      case 'month':
        startDate = startOfMonth(now);
        endDate = endOfMonth(now);
        break;
      default:
        return leads;
    }

    return leads.filter(lead => {
      // Filter by next_follow_up_date if it exists, otherwise by created_at
      const dateToCheck = lead.next_follow_up_date 
        ? new Date(lead.next_follow_up_date)
        : new Date(lead.created_at);
      
      return dateToCheck >= startDate && dateToCheck <= endDate;
    });
  };

  const getLeadsByStatus = (status: string) => {
    const statusLeads = leads.filter(lead => lead.status === status);
    return filterLeadsByDate(statusLeads);
  };

  const getPriorityBadge = (priority: string) => {
    const configs = {
      high: { variant: 'destructive' as const, icon: AlertCircle },
      medium: { variant: 'default' as const, icon: AlertCircle },
      low: { variant: 'secondary' as const, icon: AlertCircle },
    };
    const config = configs[priority as keyof typeof configs] || configs.medium;
    const Icon = config.icon;
    
    return (
      <Badge variant={config.variant} className="text-xs">
        <Icon className="w-3 h-3 mr-1" />
        {priority}
      </Badge>
    );
  };

  return (
    <div className="space-y-4">
      {/* Date Filter Buttons */}
      <div className="flex flex-wrap gap-2 items-center">
        <Calendar className="w-4 h-4 text-muted-foreground" />
        <div className="flex gap-2">
          <Button
            variant={dateFilter === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setDateFilter('all')}
          >
            All Time
          </Button>
          <Button
            variant={dateFilter === 'today' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setDateFilter('today')}
          >
            Today
          </Button>
          <Button
            variant={dateFilter === 'week' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setDateFilter('week')}
          >
            This Week
          </Button>
          <Button
            variant={dateFilter === 'month' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setDateFilter('month')}
          >
            This Month
          </Button>
        </div>
      </div>

      {/* Kanban Board */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
      {statusColumns.map(column => {
        const columnLeads = getLeadsByStatus(column.id);
        return (
          <div
            key={column.id}
            className={`glass border-2 rounded-3xl p-4 min-h-[500px] ${column.color}`}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, column.id)}
          >
            <div className="mb-4">
              <h3 className="font-bold text-lg mb-1">{column.title}</h3>
              <Badge variant="outline" className="text-xs">
                {columnLeads.length} leads
              </Badge>
            </div>
            
            <div className="space-y-3">
              {columnLeads.map(lead => (
                <Card
                  key={lead.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, lead)}
                  className={`cursor-move transition-all duration-200 glass border-border rounded-2xl ${
                    draggedLead?.id === lead.id ? 'opacity-50' : 'opacity-100'
                  }`}
                >
                  <CardContent className="p-4 space-y-3">
                    <div>
                      <h4 className="font-semibold text-sm mb-1 line-clamp-1">{lead.name}</h4>
                      {lead.company && (
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <Building className="w-3 h-3" />
                          {lead.company}
                        </p>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      {getPriorityBadge(lead.priority)}
                    </div>

                    <div className="space-y-2 text-xs text-muted-foreground">
                      <div className="flex items-center gap-1.5 truncate">
                        <Mail className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">{lead.email}</span>
                      </div>
                      {lead.phone && (
                        <div className="flex items-center gap-1.5">
                          <Phone className="w-3 h-3 flex-shrink-0" />
                          <span>{lead.phone}</span>
                        </div>
                      )}
                      {lead.next_follow_up_date && (
                        <div className="flex items-center gap-1.5">
                          <Clock className="w-3 h-3 flex-shrink-0" />
                          <span>{format(new Date(lead.next_follow_up_date), 'MMM d')}</span>
                        </div>
                      )}
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full text-xs"
                      onClick={(e) => {
                        e.stopPropagation();
                        onLeadClick(lead);
                      }}
                    >
                      <Eye className="w-3 h-3 mr-1" />
                      View Details
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        );
      })}
      </div>
    </div>
  );
}
